import React, { Component } from 'react';
import { injectIntl, defineMessages } from 'react-intl';
import { connect } from 'react-redux';
import { createFragmentContainer } from 'react-relay';
import { Link } from 'react-router-dom';

import moment from 'moment';
import PropTypes from 'prop-types';

import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import { withStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';

import IntlPropType from '../../intl';
import { logEvent, Event } from '../../lib/analytics';
import { deleteRecording } from '../../lib/api/relay/deleteRecording';
import { myRecordingsFragment } from '../../lib/api/relay/myRecordings';
import { newEvent, INFO } from '../../lib/notifications';
import {
  IconAudioTrack,
  IconDownload,
  IconPlay,
  IconTrash,
  IconVideoTrack
} from '../IconSet';
import TooltipMultiLineText from '../TooltipMultiLineText';

import MyRecordingPlayer from './MyRecordingPlayer';

import style from './style';


const messages = defineMessages({
  reportPlayRecording: { id: 'reportPlayRecording' },
  reportDeleteRecording: { id: 'reportDeleteRecording' },
  reportDownloadRecording: { id: 'reportDownloadRecording' },
  reportTypeAudioRecording: { id: 'reportTypeAudioRecording' },
  reportTypeVideoRecording: { id: 'reportTypeVideoRecording' },
});

class MyRecording extends Component {

  constructor(props) {
    super(props);
    this.state = {
      openVideoPlayer: false,
    };
    this.onCloseVideoPlayer = this.onCloseVideoPlayer.bind(this);
    this.handlePlayButton = this.handlePlayButton.bind(this);
    this.handleDeleteButton = this.handleDeleteButton.bind(this);
  }

  format_date(date) {
    if (date) {
      return moment(date).format("DD-MM-YYYY, HH:mm:ss");
    }
    return '-';
  }

  get_duration(start, end) {
    if (!start || !end) {
      return '-';
    }
    const seconds = moment(end).unix() - moment(start).unix();
    return moment.utc(seconds * 1000).format('HH:mm:ss');
  }

  handlePlayButton() {
    this.setState({ openVideoPlayer: true });
    logEvent(Event.PLAY_RECORDING);
  }

  handleDeleteButton() {
    const { relay, userId, domainId } = this.props;
    const promise = deleteRecording(relay.environment, this.props.data.id, userId, domainId);
    promise.then(
      () => {
        newEvent(INFO, 'deleteRecordingSuccess', 'deleteRecordingSuccess',
          "Delete recording success");
        this.props.onDeleteRecording();
        logEvent(Event.DELETE_RECORDING);
      })
      .catch(
        () => { });
  }

  onCloseVideoPlayer() {
    this.setState({ openVideoPlayer: false });
  }

  getDownloadFileName() {
    let dateText = "";
    const recStart = this.props.data.recStart;
    if (recStart) {
      dateText = "-" + moment(recStart).format("DD-MM-YYYY-HH-mm-ss");
    }
    return this.props.data.slug + dateText + '.' + this.props.data.format;
  }

  getDownloadCmp() {
    const link = React.forwardRef((props, _ref) => (
      <Link target="_blank"
        download={this.getDownloadFileName()}
        to={this.get_file_url()} {...props} />
    ));
    link.displayName = "LinkWithForwardedRef";
    return link;
  }

  get_file_url() {
    logEvent(Event.DOWNLOAD_RECORDING);
    return `/getfile/${this.props.data.id}`;
  }

  get_download_icon() {
    let ready = this.props.data.status;
    return (
      <TooltipMultiLineText

        placement="top"
        title={this.props.intl.formatMessage(messages.reportDownloadRecording)}>

        <ListItem
          ref={React.createRef()}
          className={this.props.classes.button}
          button
          disabled={ready !== 'ready' ? true : false}
          component={this.getDownloadCmp()}
        >
          <ListItemIcon>
            <IconDownload size={20} />
          </ListItemIcon>
        </ListItem>
      </TooltipMultiLineText>
    );
  }

  get_type_icon() {
    let type = this.props.data.type;
    let isAudio = type === 'audio' ? true : false;
    return (
      <TooltipMultiLineText
        placement="top"
        title={isAudio ? this.props.intl.formatMessage(messages.reportTypeAudioRecording) :
          this.props.intl.formatMessage(messages.reportTypeVideoRecording)}>
        <ListItem
          className={this.props.classes.button}
        >
          <ListItemIcon>
            {isAudio
              ? <IconAudioTrack size={24} />
              : <IconVideoTrack size={24} />
            }
          </ListItemIcon>
        </ListItem>
      </TooltipMultiLineText>
    );
  }

  get_play_icon() {
    let ready = this.props.data.status;
    return (
      <TooltipMultiLineText
        placement="top"
        title={this.props.intl.formatMessage(messages.reportPlayRecording)}>
        <ListItem
          className={this.props.classes.button}
          button
          disabled={ready !== 'ready' ? true : false}
          onClick={this.handlePlayButton}
        >
          <ListItemIcon>
            <IconPlay size={24} />
          </ListItemIcon>
        </ListItem>
      </TooltipMultiLineText>
    );
  }

  get_delete_icon() {
    let ready = this.props.data.status;
    return (
      <TooltipMultiLineText
        placement="top"
        title={this.props.intl.formatMessage(messages.reportDeleteRecording)}>
        <ListItem
          className={this.props.classes.button}
          button
          disabled={ready !== 'ready' ? true : false}
          onClick={this.handleDeleteButton}
        >
          <ListItemIcon>
            <IconTrash size={24} />
          </ListItemIcon>
        </ListItem>
      </TooltipMultiLineText>
    );
  }

  render() {
    const { classes } = this.props;
    let isAudio = this.props.data.type === 'audio' ? true : false;
    return (
      <React.Fragment>
        <TableRow>
          <TableCell className={classes.table_cell}>
            {this.props.data.title}
          </TableCell>
          <TableCell className={classes.table_cell}>
            {this.props.data.slug}
          </TableCell>
          <TableCell className={classes.table_cell}>
            {this.format_date(this.props.data.recStart)}
          </TableCell>
          <TableCell className={classes.table_cell}>
            {this.get_duration(this.props.data.recStart, this.props.data.recEnd)}
          </TableCell>
          <TableCell className={classes.table_cell}>
            {this.props.data.notes}
          </TableCell>
          <TableCell className={classes.table_cell}>
            {this.get_type_icon()}
          </TableCell>
          <TableCell className={classes.table_cell}>
            {this.get_play_icon()}
          </TableCell>
          <TableCell className={classes.table_cell}>
            {this.get_download_icon()}
          </TableCell>
          <TableCell className={classes.table_cell}>
            {this.get_delete_icon()}
          </TableCell>
        </TableRow>
        {this.state.openVideoPlayer === true &&
          <MyRecordingPlayer
            onCloseClick={this.onCloseVideoPlayer}
            recordingUrl={this.get_file_url()}
            isAudio={isAudio}
          />
        }
      </React.Fragment>
    );
  }
}

function mapStateToProps(state) {
  return {
    userId: state.session.userId,
    domainId: state.session.domainId,
  };
}

MyRecording.propTypes = {
  intl: IntlPropType.isRequired,
  classes: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  relay: PropTypes.object.isRequired,
  onDeleteRecording: PropTypes.func.isRequired,
  userId: PropTypes.string,
  domainId: PropTypes.string,
};

export default createFragmentContainer(
  withStyles(style)(injectIntl(connect(mapStateToProps)(MyRecording))), { data: myRecordingsFragment });
